import { ApiQueryParams } from '@frontend/api-utils';
import { CheckboxInput, DateInput, NumberInput } from '@frontend/basic-forms';
import { EnumSelectListV2, ObjectSelectMapper } from '@frontend/components/select';
import { onQueryParamChange } from '@frontend/form';
import { CommonMessage } from '@frontend/lang';
import { changeFilter, useListRepository } from '@frontend/repository';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { format } from 'date-fns';
import React from 'react';

import { FilterOptions } from '../header-filter';
import useFilterModal from './filter-modal.controller';

export interface FilterModalProps<T extends object & { id: string }> extends ReturnType<typeof useListRepository<T>> {
    dispatch: ThunkDispatch<any, any, Action>;
    filter?: FilterOptions;
    changeURLParams: (params: ApiQueryParams<string | number>) => void;
    handleClose: () => void;
}

const FilterModal = <T extends object & { id: string }>(props: FilterModalProps<T>) => {
    const viewProps = useFilterModal(props);
    return (
        <>
            <div className='border-border-default flex flex-wrap justify-between border-b-1 p-4'>
                <button
                    className='btn bg-brand-secondary text-always-white'
                    onClick={props.handleClose}>
                    {CommonMessage.BUTTONS.CLOSE}
                </button>
                {viewProps.params && Object.keys(viewProps.params).filter((p) => !p.includes('search')).length > 0 && (
                    <button
                        className='btn bg-status-danger text-always-white'
                        onClick={() => {
                            viewProps.setSearchParams((prev) => {
                                const newParams = new URLSearchParams(prev);
                                if (!viewProps.searchParams || !viewProps.params) return newParams;
                                for (const key of viewProps.searchParams) {
                                    const keys = props.filter?.queryParamKeyOverwrite?.get(key[0]) ?? key;
                                    if (Object.keys(viewProps.params).includes(keys[0])) {
                                        newParams.delete(keys[0]);
                                    }
                                }
                                return newParams;
                            });
                            props.changeURLParams({});
                            props.handleClose();
                        }}>
                        {CommonMessage.BUTTONS.CLEAR}
                    </button>
                )}
                <button
                    className='btn bg-status-success text-always-white'
                    onClick={() => {
                        viewProps.params && props.changeURLParams(viewProps.params);
                        props.handleClose();
                    }}>
                    {CommonMessage.BUTTONS.SUBMIT}
                </button>
            </div>
            <div className='flex flex-col p-4'>
                {viewProps.params &&
                    props.filter?.keys?.map((key) => {
                        if (typeof key === 'string') {
                            const extraProps = props.filter?.keyProps?.get(key) ?? {};
                            if (props.filter?.booleanKeys?.includes(key)) {
                                const filterKey = props.filter?.queryParamKeyOverwrite?.get(key.toString()) ?? key;
                                return (
                                    <CheckboxInput
                                        key={key}
                                        id={key}
                                        label={key.replace(/_/g, ' ').charAt(0).toUpperCase() + key.replace(/_/g, ' ').slice(1)}
                                        value={viewProps.params?.[filterKey] === 'true'}
                                        onChange={(value) => {
                                            props.dispatch(
                                                changeFilter({
                                                    listId: props.listId,
                                                    key: filterKey,
                                                    value: value ? 'true' : undefined
                                                })
                                            );
                                            onQueryParamChange(value ? 'true' : null, filterKey, viewProps.setSearchParams);
                                        }}
                                        {...extraProps}
                                    />
                                );
                            }
                            if ((key as string).includes('_id')) {
                                return (
                                    <ObjectSelectMapper<T>
                                        key={key}
                                        id={key}
                                        objectKey={key as keyof T}
                                        submitted={false}
                                        useConditionedStyling={false}
                                        onChange={(newValue) => {
                                            props.dispatch(
                                                changeFilter({
                                                    listId: props.listId,
                                                    key: props.filter?.queryParamKeyOverwrite?.get(key.toString()) ?? key,
                                                    value: newValue
                                                })
                                            );
                                        }}
                                        value={viewProps.params?.[props.filter?.queryParamKeyOverwrite?.get(key.toString()) ?? key]}
                                        className='me-1 w-full'
                                        isClearable
                                        allowURLChange
                                        {...extraProps}
                                        {...props}
                                    />
                                );
                            }
                            if (['from', 'to', 'timestamp'].includes(key) || key.includes('timestamp')) {
                                let keyValue = key;
                                if (key.includes('from')) keyValue = 'after';
                                if (key.includes('to')) keyValue = 'before';
                                const filterKey = props.filter?.queryParamKeyOverwrite?.get(keyValue.toString()) ?? keyValue;
                                const rawValue = viewProps.params?.[filterKey];
                                const dateValue =
                                    rawValue !== undefined && rawValue !== null && rawValue !== '' ? new Date(rawValue as string | number) : undefined;
                                return (
                                    <DateInput
                                        key={key}
                                        id={key}
                                        label={key.replace(/_/g, ' ').charAt(0).toUpperCase() + key.replace(/_/g, ' ').slice(1)}
                                        useConditionedStyling={false}
                                        onChange={(newValue) => {
                                            props.dispatch(
                                                changeFilter({
                                                    listId: props.listId,
                                                    key: filterKey,
                                                    value: newValue ? format(newValue, "yyyy-MM-dd'T'HH:mm:ss.SSSSSSXXX") : undefined
                                                })
                                            );
                                        }}
                                        value={dateValue && !isNaN(dateValue.getTime()) ? dateValue : undefined}
                                        submitted={false}
                                        className='me-1 w-100'
                                        isClearable
                                        {...extraProps}
                                        {...props}
                                    />
                                );
                            }
                            if (['row', 'column'].includes(key)) {
                                return (
                                    <NumberInput
                                        key={key}
                                        id={key}
                                        label={key}
                                        onChange={(value) => {
                                            props.dispatch(
                                                changeFilter({
                                                    listId: props.listId,
                                                    key: props.filter?.queryParamKeyOverwrite?.get(key.toString()) ?? key,
                                                    value: value.toString()
                                                })
                                            );
                                            onQueryParamChange(value.toString(), key.toString(), viewProps.setSearchParams);
                                        }}
                                        value={viewProps.params?.[props.filter?.queryParamKeyOverwrite?.get(key.toString()) ?? key]}
                                        className='me-1 w-100'
                                        isClearable
                                        allowURLChange
                                        {...extraProps}
                                        {...props}
                                    />
                                );
                            }
                        }
                        if (key in EnumSelectListV2) {
                            const EnumInput = EnumSelectListV2[key as keyof typeof EnumSelectListV2];
                            const extraProps = props.filter?.keyProps?.get(key as string) ?? {};

                            return (
                                <EnumInput
                                    key={key}
                                    id={key}
                                    isClearable
                                    value={viewProps.params?.[props.filter?.queryParamKeyOverwrite?.get(key.toString()) ?? key] as any}
                                    required={false}
                                    onSelect={(newValue) => {
                                        props.dispatch(
                                            changeFilter({
                                                listId: props.listId,
                                                key: props.filter?.queryParamKeyOverwrite?.get(key.toString()) ?? key,
                                                value: newValue?.value
                                            })
                                        );
                                        onQueryParamChange(
                                            newValue?.value ?? null,
                                            props.filter?.queryParamKeyOverwrite?.get(key.toString()) ?? key.toString(),
                                            viewProps.setSearchParams
                                        );
                                    }}
                                    className='me-1'
                                    {...extraProps}
                                />
                            );
                        }
                        return <></>;
                    })}
            </div>
        </>
    );
};

export default FilterModal;
